/**
 * PricingService - Centralized pricing and VAT calculation service
 *
 * This unified service provides a single source of truth for all pricing calculations
 * across the application, ensuring consistent VAT handling for private vs corporate customers.
 *
 * Works on both client and server side.
 */

import {
  CustomerType,
  PriceType,
  ModifierType,
  PricingBreakdown as BasePricingBreakdown,
  ShowSelection as BaseShowSelection,
  ShowPricingData as BaseShowPricingData,
  PriceModifier,
  AddOn,
} from "./types";

// Input types
export interface PricingInput {
  unitPrice: number; // From DB pricings.price (always excl VAT)
  vatPercentage: number; // From DB pricings.vat_percentage
  participants: number; // Number of people
  customerType: CustomerType;
  priceType: PriceType;
  priceModifier?: {
    type: ModifierType;
    value: number; // Percentage value
  };
  addOns?: Array<{
    price: number; // Unit price
    quantity: number;
    vatPercentage: number;
    type?: "per_person" | "fixed";
  }>;
  currency?: string;
}

// Extend base types for specific use cases
export interface ShowSelection extends BaseShowSelection {
  // Additional fields specific to this service if needed
}

export interface AddOnSelection {
  addOnId: number;
  quantity: number;
  price?: number; // Optional override
  vatPercentage?: number;
  type?: "per_person" | "fixed";
}

export interface BookingPricingParams {
  venueId: number;
  shows: ShowSelection[];
  addOns?: AddOnSelection[];
  participantCount: number;
  customerType: CustomerType;
  bookingDate: string;
  priceType?: PriceType;
  currency?: string;
}

// Re-export for backward compatibility
export type ShowPricingData = BaseShowPricingData;

export interface ModifierResult {
  totalPrice: number;
  averageVatRate: number;
  showDetails: ShowPricingData[];
  appliedModifier?: {
    type: ModifierType;
    value: number;
  };
  modifiersApplied: Array<{
    name: string;
    amount: number;
  }>;
}

export interface AddOnDetail {
  addOnId: number;
  unitPrice: number;
  quantity: number;
  vatPercentage: number;
  type: "per_person" | "fixed";
  per_person_mode_price_excluding_vat?: number;
  fixed_mode_price_excluding_vat?: number;
}

export interface AddOnsResult {
  items: AddOnDetail[];
}

export interface BookingPricingResult {
  breakdown: PricingBreakdown;
  showDetails: ShowPricingData[];
  modifiersApplied: Array<{
    name: string;
    amount: number;
  }>;
  addOnsDetails: AddOnDetail[];
  per_person_mode_price_excluding_vat?: number;
  fixed_mode_price_excluding_vat?: number;
}

// Extended PricingBreakdown with additional fields for this service
export interface PricingBreakdown extends BasePricingBreakdown {
  // Base booking amounts
  baseUnitPrice: number; // Unit price excl VAT
  baseAmount: number; // Base total excl VAT (before modifiers)

  // Modifier impact
  modifierAmount: number; // Discount/surcharge amount
  modifiedBase: number; // Base after modifier (still excl VAT)

  // VAT calculations (from base type)
  // vatRate: number;              // Already in base
  // vatAmount: number;            // Already in base

  // Add-ons
  addOnsBase: number; // Add-ons total excl VAT
  addOnsVat: number; // VAT on add-ons
  addOnsTotal: number; // Add-ons total (calculated)

  // Final totals
  bookingSubtotal: number; // Booking excl VAT (modified base)
  bookingVat: number; // VAT on booking
  bookingTotal: number; // Booking incl VAT
  grandTotal: number; // Everything combined (booking + add-ons)

  // Display helpers (some from base type)
  // displayAmount: number;        // Already in base
  // displayLabel: string;         // Already in base
  displayCurrency: string;

  // Breakdown for UI
  breakdown: {
    lineItems: Array<{
      description: string;
      amountExclVat: number;
      vatAmount: number;
      amountInclVat: number;
    }>;
  };
}

export class PricingService {
  /**
   * Primary calculation method
   * Calculates complete pricing breakdown including VAT based on customer type
   */
  static calculate(input: PricingInput): PricingBreakdown {
    // Validate input
    this.validateInput(input);

    // Calculate base amount
    const baseAmount =
      input.priceType === "fixed"
        ? input.unitPrice
        : input.unitPrice * input.participants;

    // Apply price modifier if exists
    let modifierAmount = 0;
    let modifiedBase = baseAmount;

    if (input.priceModifier) {
      modifierAmount = this.calculateModifier(baseAmount, input.priceModifier);
      modifiedBase = baseAmount + modifierAmount; // modifierAmount is negative for discounts
    }

    // Calculate VAT (always calculated, regardless of customer type)
    // For corporate customers, VAT is shown on invoice but we still track it
    const vatRate = input.vatPercentage;
    const bookingVat = PricingService.calculateVAT(modifiedBase, vatRate);

    // Calculate add-ons
    let addOnsBase = 0;
    let addOnsVat = 0;
    const addOnItems: Array<{
      description: string;
      amountExclVat: number;
      vatAmount: number;
      amountInclVat: number;
    }> = [];

    if (input.addOns && input.addOns.length > 0) {
      input.addOns.forEach((addon) => {
        const addonBase =
          addon.type === "per_person"
            ? addon.price * addon.quantity * input.participants
            : addon.price * addon.quantity;

        // Calculate VAT for all customers (corporate pays on invoice)
        const addonVat = PricingService.calculateVAT(
          addonBase,
          addon.vatPercentage,
        );

        addOnsBase += addonBase;
        addOnsVat += addonVat;

        addOnItems.push({
          description: `Add-on (${addon.quantity}x)`,
          amountExclVat: addonBase,
          vatAmount: addonVat,
          amountInclVat: addonBase + addonVat,
        });
      });
    }

    const addOnsTotal = addOnsBase + addOnsVat;

    // Calculate totals (round for display)
    const bookingSubtotal = Math.round(modifiedBase * 100) / 100;
    const bookingTotal = Math.round((bookingSubtotal + bookingVat) * 100) / 100;
    const grandTotal = Math.round((bookingTotal + addOnsTotal) * 100) / 100;

    // Determine display amount based on customer type (rounded)
    const displayAmount =
      Math.round(
        (input.customerType === "corporate"
          ? bookingSubtotal + addOnsBase // Corporate sees excl VAT
          : grandTotal) * 100,
      ) / 100; // Private sees incl VAT

    const displayLabel =
      input.customerType === "corporate" ? "excl. VAT" : "incl. VAT";

    // Build line items for breakdown
    const lineItems = [
      {
        description:
          input.priceType === "fixed"
            ? "Base Price (Fixed)"
            : `Base Price (${input.participants} participants)`,
        amountExclVat: baseAmount,
        vatAmount: PricingService.calculateVAT(baseAmount, vatRate),
        amountInclVat:
          baseAmount + PricingService.calculateVAT(baseAmount, vatRate),
      },
    ];

    if (modifierAmount !== 0) {
      lineItems.push({
        description:
          modifierAmount < 0 ? "Discount Applied" : "Surcharge Applied",
        amountExclVat: modifierAmount,
        vatAmount: PricingService.calculateVAT(modifierAmount, vatRate),
        amountInclVat:
          modifierAmount + PricingService.calculateVAT(modifierAmount, vatRate),
      });
    }

    lineItems.push(...addOnItems);

    return {
      // Base amounts
      baseUnitPrice: input.unitPrice,
      baseAmount,

      // Modifier impact
      modifierAmount,
      modifiedBase,

      // VAT calculations
      vatRate,
      vatAmount: bookingVat,
      total: bookingTotal, // Add required total field

      // Add-ons
      addOnsBase,
      addOnsVat,
      addOnsTotal,

      // Final totals
      bookingSubtotal,
      bookingVat,
      bookingTotal,
      grandTotal,

      // Display helpers
      displayAmount,
      displayLabel,
      displayCurrency: input.currency || "DKK",

      // Breakdown for UI
      breakdown: {
        lineItems,
      },
    };
  }

  /**
   * Calculate VAT amount
   * @param amount Base amount (excl VAT)
   * @param vatPercentage VAT percentage (e.g., 25 for 25%)
   * @returns VAT amount
   */
  static calculateVAT(amount: number, vatPercentage: number): number {
    return Math.round(amount * (vatPercentage / 100) * 100) / 100;
  }

  /**
   * Extract base amount from VAT-inclusive price
   * @param amountInclVAT Amount including VAT
   * @param vatPercentage VAT percentage
   * @returns Base amount excluding VAT
   */
  static extractBase(amountInclVAT: number, vatPercentage: number): number {
    // Use higher precision (4 decimals) to avoid rounding errors when multiplying by participants
    return (
      Math.round((amountInclVAT / (1 + vatPercentage / 100)) * 10000) / 10000
    );
  }

  /**
   * Apply price modifier (discount or surcharge)
   */
  private static calculateModifier(
    baseAmount: number,
    modifier: { type: "discount" | "surcharge" | "percentage"; value: number },
  ): number {
    if (modifier.type === "discount" || modifier.type === "percentage") {
      // For now, treating percentage as discount (negative value)
      return -(baseAmount * (modifier.value / 100));
    } else if (modifier.type === "surcharge") {
      return baseAmount * (modifier.value / 100);
    }
    return 0;
  }

  /**
   * Format price for display
   */
  static formatPrice(amount: number, currency: string = "DKK"): string {
    return new Intl.NumberFormat("da-DK", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  /**
   * Calculate VAT components for database storage
   * Handles both private (VAT inclusive) and corporate (VAT exclusive) pricing
   */
  static calculateVATComponents(
    totalInclVAT: number,
    vatPercentage: number,
    customerType: "private" | "corporate",
  ): { baseAmount: number; vatAmount: number } {
    if (customerType === "corporate") {
      // Corporate: total is already excl VAT, but we calculate VAT for records
      const vatAmount = PricingService.calculateVAT(
        totalInclVAT,
        vatPercentage,
      );
      return {
        baseAmount: totalInclVAT,
        vatAmount: Math.round(vatAmount * 100) / 100,
      };
    }

    // Private: extract VAT from inclusive amount
    const baseAmount = PricingService.extractBase(totalInclVAT, vatPercentage);
    const vatAmount = totalInclVAT - baseAmount;

    return {
      baseAmount: Math.round(baseAmount * 100) / 100,
      vatAmount: Math.round(vatAmount * 100) / 100,
    };
  }

  /**
   * Validate input parameters
   */
  private static validateInput(input: PricingInput): void {
    if (input.unitPrice < 0) {
      throw new Error("Unit price cannot be negative");
    }

    if (input.participants <= 0) {
      throw new Error("Participants must be greater than 0");
    }

    if (input.vatPercentage < 0 || input.vatPercentage > 100) {
      throw new Error("VAT percentage must be between 0 and 100");
    }

    if (!["private", "corporate"].includes(input.customerType)) {
      throw new Error("Customer type must be either private or corporate");
    }

    if (!["fixed", "per_person"].includes(input.priceType)) {
      throw new Error("Price type must be either fixed or per_person");
    }
  }
}

// Export helper type for database storage
export interface PricingForStorage {
  base_amount_excl_vat: number;
  vat_amount: number;
  vat_rate: number;
  total_price: number; // For backward compatibility
}

/**
 * Convert PricingBreakdown to database storage format
 */
export function toStorageFormat(
  breakdown: PricingBreakdown,
): PricingForStorage {
  return {
    base_amount_excl_vat: breakdown.bookingSubtotal,
    vat_amount: breakdown.bookingVat,
    vat_rate: breakdown.vatRate,
    total_price: breakdown.grandTotal,
  };
}
